import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sanitizes a phone number for WhatsApp links
 * Removes all non-numeric characters and adds country code (55) if needed
 */
export function sanitizePhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  // Decidir pelo TAMANHO, nunca pelo prefixo: o DDD 55 existe (RS) e um
  // celular (55) 99999-9999 tem 11 dígitos começando com "55" — checar
  // startsWith('55') pularia o DDI e geraria link para número errado.
  if (digits.length === 10 || digits.length === 11) return `55${digits}`; // nacional: DDD + número
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) return digits; // já tem DDI 55
  return digits; // tamanho inesperado: devolve os dígitos sem inventar prefixo
}

/**
 * Detects if the app is running inside an iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Detects if the app is running as a standalone PWA
 */
export function isStandalonePWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (navigator as any).standalone === true;
}

/**
 * Returns the appropriate target for external links based on context
 * Uses _top for iframe/PWA contexts to escape sandbox restrictions
 */
export function getExternalNavTarget(): '_blank' | '_top' {
  return (isInIframe() || isStandalonePWA()) ? '_top' : '_blank';
}

/**
 * Builds a proper Instagram URL
 */
export function buildInstagramUrl(value: string): string {
  if (!value) return '';
  
  // If already has http/https, return as is
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  
  // Remove @ if present and build URL
  const username = value.replace('@', '').trim();
  return `https://www.instagram.com/${username}`;
}

/**
 * Builds a proper Facebook URL
 */
export function buildFacebookUrl(value: string): string {
  if (!value) return '';
  
  // If already has http/https, return as is
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  
  // Build URL with www
  return `https://www.facebook.com/${value.trim()}`;
}

/**
 * Builds a WhatsApp URL with optional message
 */
export function buildWhatsAppUrl(numero: string, mensagem?: string): string {
  if (!numero) return '';
  
  const numeroSanitizado = sanitizePhone(numero);
  
  if (mensagem) {
    const encodedMessage = encodeURIComponent(mensagem);
    return `https://wa.me/${numeroSanitizado}?text=${encodedMessage}`;
  }
  
  return `https://wa.me/${numeroSanitizado}`;
}

/**
 * Generic external navigation with iframe/PWA fallbacks.
 *
 * Order (all synchronous, to preserve the user activation of the click):
 * 1. Try opening a new tab via `window.open(url, '_blank', 'noopener,noreferrer')`.
 *    If it returns a truthy window handle, we're done — the user stays in the app.
 * 2. If `window.open` returned null (popup blocked), fall back to a temporary
 *    anchor with `target="_blank"`, appended/clicked/removed.
 * 3. Only inside an iframe or standalone PWA — where `_blank` is typically
 *    blocked for real — escape through the top frame as a last resort:
 *    `window.top.location.href = url` (falling back to `window.location.href`
 *    on cross-origin access errors). In a normal browser tab (not iframe, not
 *    PWA) the function stops at step 2 and never navigates the current page.
 */
export function navigateExternal(url: string): void {
  if (!url) return;

  // 1. Prefer a brand-new tab.
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (win) return;

  // 2. Popup blocked — try a temporary anchor with target="_blank".
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer external';
  link.style.position = 'fixed';
  link.style.left = '-9999px';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    if (link.parentNode) {
      document.body.removeChild(link);
    }
  }, 100);

  // 3. Last resort: inside an iframe or PWA (where _blank is usually blocked),
  //    escape through the top frame. Never run this in a normal browser tab,
  //    so the current page is never navigated there.
  if (isInIframe() || isStandalonePWA()) {
    try {
      if (window.top) (window.top as Window).location.href = url;
    } catch {
      window.location.href = url;
    }
  }
}

/**
 * Opens WhatsApp with enhanced PWA/webapp compatibility
 */
export function openWhatsApp(numero: string, mensagem: string): void {
  // Validation
  const MAX_MESSAGE_LENGTH = 2000;

  if (!numero || numero.trim() === '') {
    throw new Error('Número de telefone é obrigatório');
  }
  
  if (mensagem.length > MAX_MESSAGE_LENGTH) {
    throw new Error(`Mensagem muito longa. Máximo de ${MAX_MESSAGE_LENGTH} caracteres.`);
  }
  
  const numeroSanitizado = sanitizePhone(numero);
  
  // Validate phone number has minimum required digits
  if (numeroSanitizado.length < 10) {
    throw new Error('Número de telefone inválido');
  }
  
  const url = buildWhatsAppUrl(numeroSanitizado, mensagem);

  // Delegar para a navegação externa (trata iframe, PWA e bloqueio de popup)
  navigateExternal(url);
}

/**
 * Formats a number as currency
 */
export function formatCurrency(value: number, currency: string = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(value);
}
